import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelySafranMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Safran metallic gradient - silver to dark steel
  const bgGradient = (
    <defs>
      <radialGradient id="safranGradient" cx="50%" cy="50%" r="60%">
        <stop offset="0%" style={{ stopColor: "#E0E0E0", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#808080", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#2F4F4F", stopOpacity: 1 }} />
      </radialGradient>
    </defs>
  );

  // Turbine patterns - concentric blades radiating from center
  const turbineBlades: JSX.Element[] = [];
  const bladeRings = 8;
  const bladesPerRing = [8, 12, 16, 20, 24, 28, 32, 36];
  
  bladesPerRing.forEach((bladeCount, ring) => {
    const radius = 80 + ring * 45;
    const bladeLength = 35;
    const bladeWidth = 18 - ring * 1.5;
    
    for (let b = 0; b < bladeCount; b++) {
      const angle = (b / bladeCount) * 2 * Math.PI;
      const x1 = Math.cos(angle) * radius;
      const y1 = Math.sin(angle) * radius;
      const x2 = Math.cos(angle) * (radius + bladeLength);
      const y2 = Math.sin(angle) * (radius + bladeLength);
      
      // Blade shape as tapered polygon
      const perpAngle = angle + Math.PI / 2;
      const dx = Math.cos(perpAngle) * bladeWidth / 2;
      const dy = Math.sin(perpAngle) * bladeWidth / 2;
      
      const path = `M ${x1 + dx},${y1 + dy} L ${x2 + dx * 0.3},${y2 + dy * 0.3} L ${x2 - dx * 0.3},${y2 - dy * 0.3} L ${x1 - dx},${y1 - dy} Z`;
      
      turbineBlades.push(
        <path
          key={`blade-${ring}-${b}`}
          d={path}
          fill="none"
          stroke="#C0C0C0"
          strokeWidth={1.5}
          opacity={0.4 - ring * 0.03}
        />
      );
    }
  });

  // Radial grids - precision measurement lines
  const radialGrids: JSX.Element[] = [];
  for (let r = 0; r < 24; r++) {
    const angle = (r / 24) * 2 * Math.PI;
    const x = Math.cos(angle) * 400;
    const y = Math.sin(angle) * 400;
    radialGrids.push(
      <line
        key={`radial-${r}`}
        x1={0}
        y1={0}
        x2={x}
        y2={y}
        stroke="#FFFFFF"
        strokeWidth={r % 4 === 0 ? 2 : 1}
        opacity={r % 4 === 0 ? 0.3 : 0.15}
        strokeDasharray="5,5"
      />
    );
  }

  // Concentric power circles - thrust rings
  const powerCircles: JSX.Element[] = [];
  for (let c = 1; c <= 14; c++) {
    const radius = c * 28;
    powerCircles.push(
      <circle
        key={`circle-${c}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={c % 3 === 0 ? 2.5 : 1.5}
        opacity={0.35 - c * 0.015}
      />
    );
  }

  // Metal gradient streaks - directional thrust lines
  const thrustStreaks: JSX.Element[] = [];
  for (let s = 0; s < 32; s++) {
    const angle = (s / 32) * 2 * Math.PI;
    const startR = 60;
    const endR = 380;
    const x1 = Math.cos(angle) * startR;
    const y1 = Math.sin(angle) * startR;
    const x2 = Math.cos(angle) * endR;
    const y2 = Math.sin(angle) * endR;
    
    thrustStreaks.push(
      <line
        key={`streak-${s}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#A0A0A0"
        strokeWidth={2}
        opacity={0.15}
      />
    );
  }

  // Propulsion symmetry nodes - hexagonal precision markers
  const propulsionNodes: JSX.Element[] = [];
  const nodeRadii = [120, 200, 280, 360];
  nodeRadii.forEach((nodeR, ri) => {
    for (let n = 0; n < 6; n++) {
      const angle = (n / 6) * 2 * Math.PI;
      const nx = Math.cos(angle) * nodeR;
      const ny = Math.sin(angle) * nodeR;
      
      // Hexagonal node
      const hexPoints: string[] = [];
      for (let h = 0; h < 6; h++) {
        const ha = (h / 6) * 2 * Math.PI;
        const hx = nx + Math.cos(ha) * 18;
        const hy = ny + Math.sin(ha) * 18;
        hexPoints.push(`${hx},${hy}`);
      }
      
      propulsionNodes.push(
        <g key={`node-${ri}-${n}`}>
          <polygon
            points={hexPoints.join(" ")}
            fill="none"
            stroke="#FFFFFF"
            strokeWidth={2}
            opacity={0.5}
          />
          <circle
            cx={nx}
            cy={ny}
            r={8}
            fill="#C0C0C0"
            opacity={0.3}
          />
        </g>
      );
    }
  });

  // Central turbine core
  const turbineCore = (
    <g>
      <circle cx={0} cy={0} r={55} fill="none" stroke="#FFFFFF" strokeWidth={4} opacity={0.6} />
      <circle cx={0} cy={0} r={40} fill="none" stroke="#C0C0C0" strokeWidth={3} opacity={0.5} />
      <circle cx={0} cy={0} r={25} fill="#808080" opacity={0.3} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#safranGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {thrustStreaks}
        {powerCircles}
        {radialGrids}
        {turbineBlades}
        {propulsionNodes}
        {turbineCore}
      </g>
    </svg>
  );
}
